import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Plus, Search, Users, Building2, Phone, Mail,
  ChevronUp, ChevronDown, ArrowUpDown, Brain, TrendingUp,
  Edit2, Trash2, Eye, Filter, RefreshCw, Archive, RotateCcw, Smartphone, Zap,
  Linkedin, Globe, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { MainLayout } from '../components/MainLayout';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Area,
  AreaChart
} from 'recharts';
import { ChartContainer } from '../components/ChartContainer';
import { isContactPickerSupported, pickAndImportContacts } from '../utils/contactPicker';

const RELATIONSHIP_STATUS = [
  { id: 'not_contacted', label: 'Not Contacted', color: 'bg-blue-400' },
  { id: 'cold', label: 'Cold', color: 'bg-slate-500' },
  { id: 'warm', label: 'Warm', color: 'bg-amber-500' },
  { id: 'hot', label: 'Hot', color: 'bg-red-500' },
  { id: 'client', label: 'Client', color: 'bg-emerald-500' },
  { id: 'dormant', label: 'Dormant', color: 'bg-slate-400' },
];

const LEAD_SOURCES = [
  { id: 'referral', label: 'Referral' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'cold_outreach', label: 'Cold Outreach' },
  { id: 'networking', label: 'Networking' },
  { id: 'dormant', label: 'Dormant / Re-engagement' },
  { id: 'other', label: 'Other' },
];

const SCENARIO_PROFILES = {
  referred_prospect: { label: 'Referred Prospect', color: '#10b981' },
  new_linkedin_connection: { label: 'New LinkedIn Connection', color: '#3b82f6' },
  cold_prospect: { label: 'Cold Prospect', color: '#64748b' },
  networking_contact: { label: 'Networking Contact', color: '#8b5cf6' },
  dormant_reengagement: { label: 'Dormant Re-engagement', color: '#f59e0b' },
};

const LEAD_SOURCE_TO_SCENARIO = {
  referral: 'referred_prospect',
  linkedin: 'new_linkedin_connection',
  cold_outreach: 'cold_prospect',
  networking: 'networking_contact',
  dormant: 'dormant_reengagement',
};

const SECTORS = [
  { id: 'commercial_fitout', label: 'Commercial Fit-out' },
  { id: 'commercial_office', label: 'Commercial Office' },
  { id: 'retail', label: 'Retail' },
  { id: 'residential', label: 'Residential' },
  { id: 'healthcare', label: 'Healthcare' },
  { id: 'education', label: 'Education' },
  { id: 'developer', label: 'Developer' },
  { id: 'contractor', label: 'Contractor' },
  { id: 'architect', label: 'Architect' },
  { id: 'other', label: 'Other' },
];

const SORT_OPTIONS = [
  { id: 'contact_name', label: 'Name' },
  { id: 'company_name', label: 'Company' },
  { id: 'created_at', label: 'Date Created' },
  { id: 'last_contact_date', label: 'Last Contact' },
  { id: 'lead_score', label: 'Lead Score' },
];

// SortIcon component - moved outside to prevent re-creation on each render
const SortIcon = ({ field, sortBy, sortOrder }) => {
  if (sortBy !== field) return <ArrowUpDown className="w-4 h-4 text-slate-300" />;
  return sortOrder === 'asc' 
    ? <ChevronUp className="w-4 h-4 text-[#1e3a5f]" />
    : <ChevronDown className="w-4 h-4 text-[#1e3a5f]" />;
};

export const ContactsPage = () => {
  const { authFetch, user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Permissions
  const canManageContacts = user?.permissions?.can_manage_contacts || user?.permissions?.is_director;
  const canDeleteContacts = user?.permissions?.can_delete_contacts || user?.permissions?.is_director;
  
  // Filters & Sorting - Load from localStorage for persistence
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => {
    return localStorage.getItem('contacts_status_filter') || 'all';
  });
  const [sectorFilter, setSectorFilter] = useState(() => {
    return localStorage.getItem('contacts_sector_filter') || 'all';
  });
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('contacts_sort_by') || 'contact_name';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('contacts_sort_order') || 'asc';
  });
  
  // Save sort preferences to localStorage
  useEffect(() => {
    localStorage.setItem('contacts_sort_by', sortBy);
    localStorage.setItem('contacts_sort_order', sortOrder);
    localStorage.setItem('contacts_status_filter', statusFilter);
    localStorage.setItem('contacts_sector_filter', sectorFilter);
  }, [sortBy, sortOrder, statusFilter, sectorFilter]);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [scoringId, setScoringId] = useState(null);
  const [showDeletedContacts, setShowDeletedContacts] = useState(false);
  const [deletedContacts, setDeletedContacts] = useState([]);
  
  // Sequence enrollment
  const [showSequenceModal, setShowSequenceModal] = useState(false);
  const [recommendedSequence, setRecommendedSequence] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [contactSequences, setContactSequences] = useState([]);
  
  // Form
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    position: '',
    sector: '',
    relationship_status: 'cold',
    source: '',
    notes: '',
    linkedin_url: '',
    company_website: '',
    lead_source: '',
    scenario_profile: '',
  });

  useEffect(() => {
    loadContacts();
    loadSequences();
    if (canDeleteContacts) {
      loadDeletedContacts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder, statusFilter, sectorFilter]);
  
  const loadSequences = async () => {
    try {
      const res = await authFetch('/sequences');
      if (res.ok) {
        const data = await res.json();
        setSequences(data.sequences?.filter(s => s.is_active) || []);
      }
    } catch (err) {
      console.error('Failed to load sequences:', err);
    }
  };
  
  const loadContactSequences = async (contactId) => {
    try {
      const res = await authFetch(`/contacts/${contactId}/sequences`);
      if (res.ok) {
        const data = await res.json();
        setContactSequences(data.enrollments || []);
      }
    } catch (err) {
      console.error('Failed to load contact sequences:', err);
    }
  };
  
  const handleEnrollInSequence = async (sequenceId) => {
    if (!selectedContact) return;
    try {
      const res = await authFetch(`/sequences/${sequenceId}/enroll/${selectedContact.id}`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowSequenceModal(false);
        loadContactSequences(selectedContact.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to enroll contact');
      }
    } catch (err) {
      toast.error('Failed to enroll contact');
    }
  };
  
  const handleUnenrollFromSequence = async (sequenceId) => {
    if (!selectedContact) return;
    try {
      const res = await authFetch(`/sequences/${sequenceId}/unenroll/${selectedContact.id}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Contact removed from sequence');
        loadContactSequences(selectedContact.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to remove contact');
      }
    } catch (err) {
      toast.error('Failed to remove contact');
    }
  };

  const loadRecommendedSequence = async (contactId) => {
    try {
      const res = await authFetch(`/contacts/${contactId}/recommended-sequence`);
      if (res.ok) {
        const data = await res.json();
        setRecommendedSequence(data.recommended);
      }
    } catch { setRecommendedSequence(null); }
  };

  const loadContacts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        sort_order: sortOrder,
        search: searchQuery,
        status: statusFilter,
        sector: sectorFilter,
        limit: '100'
      });
      
      const res = await authFetch(`/contacts?${params}`);
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadContacts();
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const scoreContact = async (contactId) => {
    setScoringId(contactId);
    try {
      const res = await authFetch(`/leads/${contactId}/score`, { method: 'POST' });
      if (res.ok) {
        toast.success('Lead scored');
        loadContacts();
      }
    } catch (err) {
      toast.error('Failed to score');
    } finally {
      setScoringId(null);
    }
  };

  const loadScoreHistory = async (contactId) => {
    try {
      const res = await authFetch(`/leads/${contactId}/history?limit=30`);
      if (res.ok) {
        const data = await res.json();
        setScoreHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const openHistory = async (contact) => {
    setSelectedContact(contact);
    await loadScoreHistory(contact.id);
    setShowHistoryModal(true);
  };

  const createContact = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch('/pipeline/contacts', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        toast.success('Contact created');
        setShowAddModal(false);
        setFormData({
          company_name: '',
          contact_name: '',
          email: '',
          phone: '',
          position: '',
          sector: '',
          relationship_status: 'cold',
          source: '',
          notes: '',
    linkedin_url: '',
    company_website: '',
        });
        loadContacts();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create contact');
      }
    } catch (err) {
      toast.error('Failed to create contact');
    }
  };

  const deleteContact = async (contactId) => {
    if (!canDeleteContacts) {
      toast.error('You do not have permission to delete contacts');
      return;
    }
    if (!window.confirm('Are you sure you want to permanently delete this contact? This action cannot be undone.')) return;
    try {
      const res = await authFetch(`/pipeline/contacts/${contactId}/permanent`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Contact deleted permanently');
        loadContacts();
        setShowDetailModal(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete contact');
      }
    } catch (err) {
      toast.error('Failed to delete contact');
    }
  };

  const restoreContact = async (contactId) => {
    if (!canDeleteContacts) {
      toast.error('You do not have permission to restore contacts');
      return;
    }
    try {
      const res = await authFetch(`/pipeline/contacts/${contactId}/restore`, { method: 'POST' });
      if (res.ok) {
        toast.success('Contact restored successfully');
        loadContacts();
        loadDeletedContacts();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to restore contact');
      }
    } catch (err) {
      toast.error('Failed to restore');
    }
  };

  const loadDeletedContacts = async () => {
    if (!canDeleteContacts) return;
    try {
      const res = await authFetch('/pipeline/contacts/deleted');
      if (res.ok) {
        const data = await res.json();
        setDeletedContacts(data.contacts || []);
      }
    } catch (err) {
      console.error('Failed to load deleted contacts');
    }
  };

  const openEditModal = (contact) => {
    if (!canManageContacts) {
      toast.error('You do not have permission to edit contacts');
      return;
    }
    setEditingContact(contact);
    setFormData({
      company_name: contact.company_name || '',
      contact_name: contact.contact_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      position: contact.position || '',
      sector: contact.sector || '',
      relationship_status: contact.relationship_status || 'cold',
      source: contact.source || '',
      notes: contact.notes || '',
      linkedin_url: contact.linkedin_url || '',
      company_website: contact.company_website || '',
      lead_source: contact.lead_source || '',
      scenario_profile: contact.scenario_profile || '',
    });
    setShowEditModal(true);
    setShowDetailModal(false);
  };

  const updateContact = async (e) => {
    e.preventDefault();
    if (!editingContact) return;
    
    try {
      const res = await authFetch(`/pipeline/contacts/${editingContact.id}`, {
        method: 'PATCH',
        body: formData
      });
      if (res.ok) {
        toast.success('Contact updated');
        setShowEditModal(false);
        setEditingContact(null);
        setFormData({
          company_name: '',
          contact_name: '',
          email: '',
          phone: '',
          position: '',
          sector: '',
          relationship_status: 'cold',
          source: '',
          notes: '',
    linkedin_url: '',
    company_website: '',
        });
        loadContacts();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update contact');
      }
    } catch (err) {
      toast.error('Failed to update contact');
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="contacts-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
              Contacts
            </h1>
            <p className="text-slate-500 mt-1">
              {total} contacts • Manage your business relationships
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Mobile Contact Import Button - only shows on supported devices */}
            {isContactPickerSupported() && (
              <Button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('opsflow_token');
                    const result = await pickAndImportContacts(token);
                    toast.success(result.message);
                    if (result.imported_count > 0) {
                      loadContacts();
                    }
                  } catch (err) {
                    if (err.message?.includes('picker was interrupted')) {
                      toast.info('Contact selection cancelled');
                    } else {
                      toast.error(err.message || 'Failed to import contacts');
                    }
                  }
                }}
                variant="outline"
                className="rounded-full"
                data-testid="import-mobile-contacts-btn"
              >
                <Smartphone className="w-4 h-4 mr-2" />
                Import from Phone
              </Button>
            )}
            
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-6"
              data-testid="add-contact-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search contacts..."
                  className="pl-10 h-10 rounded-xl"
                  data-testid="search-input"
                />
              </div>
            </form>
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 h-10 rounded-xl" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {RELATIONSHIP_STATUS.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Sector Filter */}
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger className="w-28 md:w-40 h-10 rounded-xl text-xs md:text-sm" data-testid="sector-filter">
                <Building2 className="w-4 h-4 mr-1 md:mr-2 text-slate-400" />
                <SelectValue placeholder="Sector" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {SECTORS.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Sort By */}
            <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setSortOrder('desc'); }}>
              <SelectTrigger className="w-28 md:w-40 h-10 rounded-xl text-xs md:text-sm" data-testid="sort-by">
                <ArrowUpDown className="w-4 h-4 mr-1 md:mr-2 text-slate-400" />
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Sort Order Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="rounded-xl h-10 px-3"
            >
              {sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            
            {/* Archive Toggle (Director only) */}
            {canDeleteContacts && (
              <Button
                variant={showDeletedContacts ? "default" : "outline"}
                size="sm"
                onClick={() => setShowDeletedContacts(!showDeletedContacts)}
                className={`rounded-xl h-10 px-3 gap-2 ${showDeletedContacts ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                data-testid="toggle-archived-btn"
              >
                <Archive className="w-4 h-4" />
                <span className="hidden md:inline">Archived ({deletedContacts.length})</span>
              </Button>
            )}
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-slate-400">Loading contacts...</div>
            </div>
          ) : contacts.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No contacts found</p>
            </div>
          ) : (
            contacts.map(contact => {
              const status = RELATIONSHIP_STATUS.find(s => s.id === contact.relationship_status);
              return (
                <div 
                  key={contact.id}
                  className="bg-white rounded-2xl border border-slate-100 p-4"
                  data-testid={`contact-card-${contact.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${status?.color || 'bg-slate-400'}`}>
                      {contact.contact_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-[#1e3a5f] truncate">{contact.contact_name}</p>
                        {contact.lead_score && (
                          <span className={`score-badge score-badge-${contact.lead_grade?.[0]?.toLowerCase() || 'c'} text-xs w-8 h-8`}>
                            {contact.lead_grade}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 truncate">{contact.company_name}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status?.color || 'bg-slate-400'} text-white`}>
                          {status?.label || 'Unknown'}
                        </span>
                        {contact.sector && (
                          <span className="text-xs text-slate-400 capitalize">{contact.sector?.replace('_', ' ')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <div className="text-xs text-slate-500">
                      {contact.last_contact_date 
                        ? `Last: ${new Date(contact.last_contact_date).toLocaleDateString('en-GB')}`
                        : 'Never contacted'}
                    </div>
                    <div className="flex items-center gap-1">
                      {!contact.lead_score && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-violet-600 h-8 px-2"
                          onClick={() => scoreContact(contact.id)}
                          disabled={scoringId === contact.id}
                        >
                          {scoringId === contact.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Brain className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-500 h-8 px-2"
                        onClick={() => {
                          setSelectedContact(contact);
                          loadContactSequences(contact.id);
                          loadRecommendedSequence(contact.id);
                          setShowDetailModal(true);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 h-8 px-2"
                        onClick={() => deleteContact(contact.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th 
                    className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                    onClick={() => toggleSort('contact_name')}
                  >
                    <div className="flex items-center gap-2">
                      Contact <SortIcon field="contact_name" sortBy={sortBy} sortOrder={sortOrder} />
                    </div>
                  </th>
                  <th 
                    className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                    onClick={() => toggleSort('company_name')}
                  >
                    <div className="flex items-center gap-2">
                      Company <SortIcon field="company_name" sortBy={sortBy} sortOrder={sortOrder} />
                    </div>
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th 
                    className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                    onClick={() => toggleSort('last_contact_date')}
                  >
                    <div className="flex items-center gap-2">
                      Last Contact <SortIcon field="last_contact_date" sortBy={sortBy} sortOrder={sortOrder} />
                    </div>
                  </th>
                  <th 
                    className="text-center px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                    onClick={() => toggleSort('lead_score')}
                  >
                    <div className="flex items-center justify-center gap-2">
                      Score <SortIcon field="lead_score" sortBy={sortBy} sortOrder={sortOrder} />
                    </div>
                  </th>
                  <th 
                    className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                    onClick={() => toggleSort('created_at')}
                  >
                    <div className="flex items-center gap-2">
                      Created <SortIcon field="created_at" sortBy={sortBy} sortOrder={sortOrder} />
                    </div>
                  </th>
                  <th className="text-right px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="animate-pulse text-slate-400">Loading contacts...</div>
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No contacts found</p>
                    </td>
                  </tr>
                ) : (
                  contacts.map(contact => {
                    const status = RELATIONSHIP_STATUS.find(s => s.id === contact.relationship_status);
                    return (
                      <tr 
                        key={contact.id}
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`contact-row-${contact.id}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${status?.color || 'bg-slate-400'}`}>
                              {contact.contact_name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-[#1e3a5f]">{contact.contact_name}</p>
                              {contact.position && (
                                <p className="text-xs text-slate-500">{contact.position}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700">{contact.company_name}</p>
                          {contact.sector && (
                            <p className="text-xs text-slate-400 capitalize">{contact.sector?.replace('_', ' ')}</p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${status?.color || 'bg-slate-400'} text-white`}>
                            {status?.label || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-600">
                            {contact.last_contact_date 
                              ? new Date(contact.last_contact_date).toLocaleDateString('en-GB')
                              : 'Never'}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {contact.lead_score ? (
                            <button
                              onClick={() => openHistory(contact)}
                              className="inline-flex items-center gap-1.5 hover:scale-105 transition-transform cursor-pointer"
                            >
                              <span className={`score-badge score-badge-${contact.lead_grade?.[0]?.toLowerCase() || 'c'}`}>
                                {contact.lead_grade}
                              </span>
                              <TrendingUp className="w-3 h-3 text-slate-400" />
                            </button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                              onClick={() => scoreContact(contact.id)}
                              disabled={scoringId === contact.id}
                            >
                              {scoringId === contact.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Brain className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-500">
                            {contact.created_at 
                              ? new Date(contact.created_at).toLocaleDateString('en-GB')
                              : '-'}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-500 hover:text-[#1e3a5f]"
                              onClick={() => {
                                setSelectedContact(contact);
                                loadContactSequences(contact.id);
                                loadRecommendedSequence(contact.id);
                                setShowDetailModal(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-500 hover:text-red-600"
                              onClick={() => deleteContact(contact.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Archived Contacts Section (Director only) */}
        {showDeletedContacts && canDeleteContacts && (
          <div className="bg-amber-50 rounded-2xl border border-amber-200 shadow-sm overflow-hidden">
            <div className="p-4 bg-amber-100 border-b border-amber-200">
              <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                <Archive className="w-5 h-5" />
                Archived Contacts ({deletedContacts.length})
              </h3>
              <p className="text-sm text-amber-600 mt-1">These contacts have been archived and are hidden from regular views.</p>
            </div>
            {deletedContacts.length === 0 ? (
              <div className="p-8 text-center text-amber-600">
                No archived contacts
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-amber-100/50 border-b border-amber-200">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-amber-700 uppercase">Contact</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-amber-700 uppercase">Company</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-amber-700 uppercase">Archived On</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-amber-700 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100">
                    {deletedContacts.map(contact => (
                      <tr key={contact.id} className="hover:bg-amber-50/50" data-testid={`archived-contact-${contact.id}`}>
                        <td className="px-6 py-4">
                          <p className="font-medium text-amber-900">{contact.contact_name}</p>
                          {contact.position && <p className="text-xs text-amber-600">{contact.position}</p>}
                        </td>
                        <td className="px-6 py-4 text-sm text-amber-700">{contact.company_name}</td>
                        <td className="px-6 py-4 text-sm text-amber-600">
                          {contact.deleted_at 
                            ? new Date(contact.deleted_at).toLocaleDateString('en-GB')
                            : '-'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => restoreContact(contact.id)}
                            className="rounded-xl text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                            data-testid={`restore-btn-${contact.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Restore
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Add Contact Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f]">
                Add Contact
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={createContact} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name *</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                    required
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company *</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    required
                    className="rounded-xl"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Position</Label>
                  <Input
                    value={formData.position}
                    onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sector</Label>
                  <Select
                    value={formData.sector}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, sector: value }))}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTORS.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>LinkedIn Profile</Label>
                  <Input
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                    placeholder="https://linkedin.com/in/..."
                    className="rounded-xl"
                    data-testid="create-linkedin"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company Website</Label>
                  <Input
                    value={formData.company_website}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_website: e.target.value }))}
                    placeholder="https://..."
                    className="rounded-xl"
                    data-testid="create-website"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Lead Source</Label>
                <Select
                  value={formData.lead_source}
                  onValueChange={(v) => setFormData(prev => ({
                    ...prev,
                    lead_source: v,
                    scenario_profile: LEAD_SOURCE_TO_SCENARIO[v] || prev.scenario_profile
                  }))}
                >
                  <SelectTrigger className="rounded-xl" data-testid="create-lead-source">
                    <SelectValue placeholder="Select lead source" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_SOURCES.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.lead_source && LEAD_SOURCE_TO_SCENARIO[formData.lead_source] && (
                  <p className="text-xs text-slate-500 mt-1">
                    Scenario: <span className="font-medium" style={{ color: SCENARIO_PROFILES[LEAD_SOURCE_TO_SCENARIO[formData.lead_source]]?.color }}>
                      {SCENARIO_PROFILES[LEAD_SOURCE_TO_SCENARIO[formData.lead_source]]?.label}
                    </span>
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="min-h-[80px] rounded-xl"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full">
                  Add Contact
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Contact Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-md max-h-[85dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f]">
                Edit Contact
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={updateContact} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={formData.company_name}
                    onChange={e => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Acme Ltd"
                    required
                    data-testid="edit-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Name *</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={e => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="John Smith"
                    required
                    data-testid="edit-contact-name"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@acme.com"
                    data-testid="edit-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+44 7700 900000"
                    data-testid="edit-phone"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Position</Label>
                  <Input
                    value={formData.position}
                    onChange={e => setFormData({ ...formData, position: e.target.value })}
                    placeholder="Project Manager"
                    data-testid="edit-position"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sector</Label>
                  <Select
                    value={formData.sector}
                    onValueChange={v => setFormData({ ...formData, sector: v })}
                  >
                    <SelectTrigger data-testid="edit-sector">
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="commercial_fitout">Commercial Fit-Out</SelectItem>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="education">Education</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Relationship Status</Label>
                <Select
                  value={formData.relationship_status}
                  onValueChange={v => setFormData({ ...formData, relationship_status: v })}
                >
                  <SelectTrigger data-testid="edit-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_contacted">Not Contacted</SelectItem>
                    <SelectItem value="cold">Cold</SelectItem>
                    <SelectItem value="warm">Warm</SelectItem>
                    <SelectItem value="hot">Hot</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="past_client">Past Client</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>LinkedIn Profile</Label>
                  <Input
                    value={formData.linkedin_url}
                    onChange={e => setFormData({ ...formData, linkedin_url: e.target.value })}
                    placeholder="https://linkedin.com/in/..."
                    className="rounded-xl"
                    data-testid="edit-linkedin"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company Website</Label>
                  <Input
                    value={formData.company_website}
                    onChange={e => setFormData({ ...formData, company_website: e.target.value })}
                    placeholder="https://..."
                    className="rounded-xl"
                    data-testid="edit-website"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Lead Source</Label>
                <Select
                  value={formData.lead_source}
                  onValueChange={v => setFormData({
                    ...formData,
                    lead_source: v,
                    scenario_profile: LEAD_SOURCE_TO_SCENARIO[v] || formData.scenario_profile
                  })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="edit-lead-source">
                    <SelectValue placeholder="Select lead source" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_SOURCES.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.lead_source && LEAD_SOURCE_TO_SCENARIO[formData.lead_source] && (
                  <p className="text-xs text-slate-500 mt-1">
                    Scenario: <span className="font-medium" style={{ color: SCENARIO_PROFILES[LEAD_SOURCE_TO_SCENARIO[formData.lead_source]]?.color }}>
                      {SCENARIO_PROFILES[LEAD_SOURCE_TO_SCENARIO[formData.lead_source]]?.label}
                    </span>
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  className="w-full p-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none"
                  rows={3}
                  data-testid="edit-notes"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full"
                  data-testid="save-contact-btn"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Lead Score History Modal */}
        <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f] flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-violet-600" />
                Lead Score History
              </DialogTitle>
            </DialogHeader>
            
            {selectedContact && (
              <div className="space-y-6 mt-4">
                {/* Contact Info */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-xl flex items-center justify-center text-white text-lg font-bold">
                    {selectedContact.contact_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-[#1e3a5f]">{selectedContact.contact_name}</p>
                    <p className="text-sm text-slate-500">{selectedContact.company_name}</p>
                  </div>
                  {selectedContact.lead_grade && (
                    <div className="ml-auto">
                      <span className={`score-badge score-badge-${selectedContact.lead_grade?.[0]?.toLowerCase() || 'c'} text-lg w-14 h-14`}>
                        {selectedContact.lead_grade}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Score Chart */}
                {scoreHistory.length > 0 ? (
                  <ChartContainer height={256}>
                    <AreaChart data={scoreHistory}>
                      <defs>
                        <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1e3a5f" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#1e3a5f" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="recorded_at" 
                        tickFormatter={(v) => new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        stroke="#94a3b8"
                        fontSize={12}
                      />
                      <YAxis 
                        domain={[0, 100]} 
                        stroke="#94a3b8"
                        fontSize={12}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'white', 
                          border: '1px solid #e2e8f0', 
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                        labelFormatter={(v) => new Date(v).toLocaleDateString('en-GB')}
                        formatter={(value, _name) => [value, 'Score']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="total_score" 
                        stroke="#1e3a5f" 
                        strokeWidth={2}
                        fill="url(#scoreGradient)"
                      />
                    </AreaChart>
                  </ChartContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center bg-slate-50 rounded-xl">
                    <div className="text-center">
                      <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">No score history yet</p>
                      <p className="text-sm text-slate-400">Score this contact to start tracking</p>
                    </div>
                  </div>
                )}
                
                {/* Score Breakdown Over Time */}
                {scoreHistory.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-slate-700">Latest Score Breakdown</h4>
                    <div className="grid grid-cols-4 gap-3">
                      {['engagement', 'fit', 'intent', 'relationship'].map(key => {
                        const latest = scoreHistory[scoreHistory.length - 1];
                        const score = latest?.breakdown?.[key] || 0;
                        return (
                          <div key={key} className="bg-slate-50 rounded-xl p-3 text-center">
                            <p className="text-xs text-slate-500 capitalize mb-1">{key}</p>
                            <p className="text-xl font-bold text-[#1e3a5f]">{score}</p>
                            <p className="text-xs text-slate-400">/25</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <Button
                  onClick={() => setShowHistoryModal(false)}
                  className="w-full bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full"
                >
                  Close
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Contact Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f]">
                Contact Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedContact && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
                    {selectedContact.contact_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-lg text-[#1e3a5f]">{selectedContact.contact_name}</p>
                    <p className="text-slate-500">{selectedContact.company_name}</p>
                    {selectedContact.position && (
                      <p className="text-sm text-slate-400">{selectedContact.position}</p>
                    )}
                  </div>
                </div>
                
                {/* Scenario Profile Badge + Lead Source */}
                {(selectedContact.scenario_profile || selectedContact.lead_source) && (
                  <div className="flex flex-wrap gap-2">
                    {selectedContact.scenario_profile && SCENARIO_PROFILES[selectedContact.scenario_profile] && (
                      <span
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-white"
                        style={{ backgroundColor: SCENARIO_PROFILES[selectedContact.scenario_profile].color }}
                        data-testid="scenario-badge"
                      >
                        {SCENARIO_PROFILES[selectedContact.scenario_profile].label}
                      </span>
                    )}
                    {selectedContact.lead_source && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                        {LEAD_SOURCES.find(s => s.id === selectedContact.lead_source)?.label || selectedContact.lead_source}
                      </span>
                    )}
                    {selectedContact.sequence_status && selectedContact.sequence_status !== 'not_enrolled' && (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        selectedContact.sequence_status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                        selectedContact.sequence_status === 'completed' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        Sequence: {selectedContact.sequence_status}
                      </span>
                    )}
                  </div>
                )}
                
                <div className="space-y-2">
                  {selectedContact.email && (
                    <a href={`mailto:${selectedContact.email}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="text-sm">{selectedContact.email}</span>
                    </a>
                  )}
                  {selectedContact.phone && (
                    <a href={`tel:${selectedContact.phone}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span className="text-sm">{selectedContact.phone}</span>
                    </a>
                  )}
                  {selectedContact.linkedin_url && (
                    <a href={selectedContact.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-blue-50">
                      <Linkedin className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-700 truncate">{selectedContact.linkedin_url.replace(/https?:\/\/(www\.)?linkedin\.com\/in\//i, '')}</span>
                    </a>
                  )}
                  {selectedContact.company_website && (
                    <a href={selectedContact.company_website.startsWith('http') ? selectedContact.company_website : `https://${selectedContact.company_website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50">
                      <Globe className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-emerald-700 truncate">{selectedContact.company_website.replace(/https?:\/\/(www\.)?/i, '')}</span>
                    </a>
                  )}
                </div>
                
                {selectedContact.notes && (
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-600">{selectedContact.notes}</p>
                  </div>
                )}
                
                {/* Recommended Sequence */}
                {recommendedSequence && !contactSequences.some(e => e.status === 'active' && e.sequence_id === recommendedSequence.sequence_id) && (
                  <div className="p-3 rounded-xl border-2 border-dashed" style={{ borderColor: recommendedSequence.profile?.color || '#1e3a5f', backgroundColor: `${recommendedSequence.profile?.color || '#1e3a5f'}08` }}>
                    <p className="text-xs font-medium text-slate-500 mb-1">Recommended Sequence</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-sm" style={{ color: recommendedSequence.profile?.color }}>{recommendedSequence.sequence_name}</p>
                        <p className="text-xs text-slate-500">{recommendedSequence.steps_count} steps</p>
                      </div>
                      <Button
                        size="sm"
                        className="rounded-lg text-xs text-white"
                        style={{ backgroundColor: recommendedSequence.profile?.color || '#1e3a5f' }}
                        onClick={() => handleEnrollInSequence(recommendedSequence.sequence_id)}
                        data-testid="enrol-recommended-btn"
                      >
                        <Zap className="w-3 h-3 mr-1" />
                        Enrol Now
                      </Button>
                    </div>
                  </div>
                )}

                {/* Sequence Enrollment Section */}
                {sequences.length > 0 && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-800 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-amber-500" />
                        Sequences
                      </h4>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-lg text-xs"
                        onClick={() => {
                          loadContactSequences(selectedContact.id);
                          setShowSequenceModal(true);
                        }}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Enroll
                      </Button>
                    </div>
                    {contactSequences.filter(e => e.status === 'active').length > 0 ? (
                      <div className="space-y-2">
                        {contactSequences.filter(e => e.status === 'active').map(enrollment => (
                          <div key={enrollment.id} className="flex items-center justify-between bg-amber-50 p-2 rounded-lg text-xs">
                            <span className="text-amber-800">{enrollment.sequence_name}</span>
                            <span className="text-amber-600">Step {enrollment.current_step + 1}/{enrollment.total_steps}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400">Not enrolled in any sequences</p>
                    )}
                  </div>
                )}
                
                <div className="flex flex-wrap gap-3">
                  {canManageContacts && (
                    <Button
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={() => openEditModal(selectedContact)}
                      data-testid="edit-contact-btn"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="flex-1 rounded-full"
                    onClick={() => {
                      setShowDetailModal(false);
                      openHistory(selectedContact);
                    }}
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    History
                  </Button>
                  {selectedContact?.sequence_status === 'active' && (
                    <Button
                      variant="outline"
                      className="flex-1 rounded-full text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      onClick={async () => {
                        try {
                          const res = await authFetch(`/contacts/${selectedContact.id}/mark-engaged`, { method: 'POST' });
                          if (res.ok) {
                            const data = await res.json();
                            toast.success(data.message);
                            loadContacts();
                            setShowDetailModal(false);
                          }
                        } catch { toast.error('Failed'); }
                      }}
                      data-testid="mark-engaged-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Engaged
                    </Button>
                  )}
                  {canDeleteContacts && (
                    <Button
                      variant="destructive"
                      className="rounded-full"
                      onClick={() => deleteContact(selectedContact.id)}
                      data-testid="delete-contact-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Sequence Enrollment Modal */}
        <Dialog open={showSequenceModal} onOpenChange={setShowSequenceModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Enroll in Sequence</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-slate-500 mb-4">
                Select a sequence to enroll <strong>{selectedContact?.contact_name}</strong> in:
              </p>
              
              {/* Already enrolled sequences */}
              {contactSequences.filter(e => e.status === 'active').length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-slate-500 mb-2">Currently Enrolled</h4>
                  <div className="space-y-2">
                    {contactSequences.filter(e => e.status === 'active').map(enrollment => (
                      <div key={enrollment.id} className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-green-800">{enrollment.sequence_name}</p>
                          <p className="text-xs text-green-600">Step {enrollment.current_step + 1}/{enrollment.total_steps}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnenrollFromSequence(enrollment.sequence_id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Available sequences */}
              <h4 className="text-xs font-medium text-slate-500 mb-2">Available Sequences</h4>
              <div className="space-y-2">
                {sequences
                  .filter(s => !contactSequences.some(e => e.sequence_id === s.id && e.status === 'active'))
                  .map(sequence => (
                  <div
                    key={sequence.id}
                    className="p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => handleEnrollInSequence(sequence.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{sequence.name}</p>
                        <p className="text-xs text-slate-500">{sequence.steps?.length || 0} steps</p>
                      </div>
                      <Zap className="w-4 h-4 text-amber-500" />
                    </div>
                    {sequence.description && (
                      <p className="text-xs text-slate-400 mt-1">{sequence.description}</p>
                    )}
                  </div>
                ))}
                {sequences.filter(s => !contactSequences.some(e => e.sequence_id === s.id && e.status === 'active')).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No available sequences</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};
